import { Rational } from "@tento-core/rational";
import { Network } from "../network.ts";

/**
 * The typed `Network.SheetRowDb -> Network.SheetRow` projection for the
 * backend row-metadata cache (`Backend.storeRowMeta` / `rowMeta`).
 *
 * A `Network.SheetRow` is the row's own fields (`SheetRowDb`) plus its place
 * in one sheet (`SheetRowEdge`: `position`, `children`). The cache is typed on
 * the whole `SheetRow`, but every reader consults only the `SheetRowDb` half
 * (type, lookupKey, attributes, metadata: `RowCellCache.applyMeta`, the fetch
 * coverage decisions, alias encoding inheritance). Rows reach the cache from
 * places that carry no edge (a standalone `GET /api/rows/{rowKey}`, a bulk
 * cell response, a row known only by its type), so those callers say so with
 * `SheetRowMeta.DETACHED` instead of asserting a `SheetRow` they do not hold.
 *
 * Internal; not exported from the package barrel.
 */
export namespace SheetRowMeta {
  /**
   * The edge of a row read outside any sheet: the first position, no
   * children. Nothing reads the edge back out of the metadata cache.
   */
  export const DETACHED: Network.SheetRowEdge = Object.freeze({
    position: Rational.first(),
    children: null,
  });

  /** Join a row's own fields with its edge. */
  export function of(
    db: Network.SheetRowDb,
    edge: Network.SheetRowEdge,
  ): Network.SheetRow {
    return { ...db, position: edge.position, children: edge.children };
  }

  /**
   * A row known only from its own fields, with no server copy to read (an
   * ephemeral sheet's seeded row): those fields made into a `SheetRowDb`,
   * on the detached edge.
   */
  export function fromFields(fields: Parameters<typeof Network.SheetRowDb.make>[0]): Network.SheetRow {
    return of(Network.SheetRowDb.make(fields), DETACHED);
  }
}
